<template>
  <div class="form">
    <loading :active.sync="getIsLoading" :can-cancel="false"></loading>
    <!-- Recently Shared Files Section (shown when IDs are present from document shared task) -->
    <div v-if="recentlySharedFiles.length > 0" class="mb-5">
      <h4 class="mb-3 f-weight-550">Recently Shared Files</h4>
      <table class="table mb-5 tbl-bordered table-striped">
        <thead>
          <tr class="border_bottom">
            <th>File Name</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in recentlySharedFiles" :key="item.fileID">
            <td>
              <a @click.prevent="getFile(item.fileID)" href="">{{
                item.description
              }}</a>
            </td>
            <td>
              <span>{{ date(item.fileDate) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- All Documents Section -->
    <div v-if="doclist && doclist.length > 0">
      <h4 v-if="recentlySharedFiles.length > 0" class="mb-3 f-weight-550">All Documents</h4>
      <table class="table mb-5 tbl-bordered table-striped">
        <thead>
          <tr class="border_bottom">
            <th>File Name</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in doclist" :key="item.fileID">
            <td>
              <a @click.prevent="getFile(item.fileID)" href="">{{
                item.description
              }}</a>
            </td>
            <td>
              <span>{{ date(item.fileDate) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Empty state -->
    <div v-if="!doclist || doclist.length === 0" class="text-center py-5">
      <p>No documents found.</p>
    </div>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import api from '../../api/http-common.js'
import { mapGetters } from 'vuex'
import moment from 'moment'

export default {
  components: {
    Loading
  },
  data() {
    return {
      doclist: null,
      dt: null,
      recentlySharedFiles: [],
      allDocuments: []
    }
  },
  computed: {
    ...mapGetters([
      'getProfilePatientAssociated',
      'getIsLoading',
      'currentPatientAssociated'
    ])
  },
  methods: {
    date: function (date) {
      return moment(date).format('MM/DD/YYYY')
    },
    async getFile(fileID) {
      try {
        // Show loading state
        this.$store.dispatch('setIsLoading', true)

        const file = await api.patient.request.downloadfilebase64({
          facilityId: this.currentPatientAssociated.facilityID,
          id: fileID
        })
        // Convert base64 to binary
        const byteCharacters = atob(file.data.base64)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)

        // Determine MIME type based on file extension
        const getMimeType = (filename) => {
          const extension = filename.split('.').pop().toLowerCase()
          const mimeTypes = {
            pdf: 'application/pdf',
            doc: 'application/msword',
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            xls: 'application/vnd.ms-excel',
            xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            txt: 'text/plain',
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            png: 'image/png',
            gif: 'image/gif',
            bmp: 'image/bmp',
            tiff: 'image/tiff',
            csv: 'text/csv',
            rtf: 'application/rtf',
            zip: 'application/zip',
            rar: 'application/x-rar-compressed'
          }
          return mimeTypes[extension] || file.data.mimeType || 'application/octet-stream'
        }

        const filename = file.data.originalFileName || `document_${fileID}`
        const mimeType = getMimeType(filename)
        const blob = new Blob([byteArray], { type: mimeType })

        // Create download link with proper filename
        const url = URL.createObjectURL(blob)

        // Use a more reliable download method
        this.downloadFile(url, filename)

        // Clean up the object URL after a short delay
        setTimeout(() => {
          URL.revokeObjectURL(url)
        }, 100)
      } catch (error) {
        console.error('Download failed:', error)
      } finally {
        // Hide loading state
        this.$store.dispatch('setIsLoading', false)
      }
    },
    downloadFile(url, filename) {
      // Create and configure download link
      const link = document.createElement('a')
      link.href = url
      link.download = filename

      // Ensure the link is styled to be invisible
      link.style.display = 'none'

      // Add to document, click, and remove immediately
      document.body.appendChild(link)

      // Trigger download - this must happen synchronously
      link.click()

      // Clean up immediately
      document.body.removeChild(link)
    },

    // Alternative method if direct download fails
    downloadFileAlternative(url, filename) {
      // If direct download doesn't work, open in new tab
      // User can then right-click and save as
      const newWindow = window.open(url, '_blank')
      if (!newWindow) {
        // If popup blocked, use fallback
        window.location.href = url
      }
    },

    // Combined download method with fallback
    async downloadWithFallback(fileID) {
      try {
        await this.getFile(fileID)
      } catch (error) {
        console.error('Primary download failed, trying alternative:', error)
        // If the main download fails, you could implement a fallback here
        // For example, redirect to a server endpoint that handles the download
        window.open(`/api/download/${fileID}`, '_blank')
      }
    },
    processDocuments() {
      // Check for IDs in route query (used by FV tasks - document shared tasks)
      // Example: /menu/documents?ids=2539,2540
      const idsParam = this.$route && this.$route.query ? this.$route.query.ids : ''
      if (typeof idsParam === 'string' && idsParam.trim() !== '') {
        const allowedIds = new Set(
          idsParam
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .map((s) => parseInt(s, 10))
            .filter((n) => Number.isFinite(n) && n > 0)
        )
        // Filter recently shared files based on the IDs from the task
        this.recentlySharedFiles = this.allDocuments.filter((d) => allowedIds.has(d.fileID))
        // Show all documents in the main list (including the shared ones)
        // If you want to exclude shared files from the main list, use:
        // this.doclist = this.allDocuments.filter((d) => !allowedIds.has(d.fileID))
        this.doclist = this.allDocuments
      } else {
        // No IDs in query, show all documents normally
        this.doclist = this.allDocuments
        this.recentlySharedFiles = []
      }
    }
  },
  watch: {
    '$route.query.ids'() {
      // Re-process documents when route query changes
      this.processDocuments()
    }
  },
  async mounted() {
    const res = await api.patient.request.getattachmentlist({
      facilityId: this.currentPatientAssociated.facilityID,
      patientId: this.currentPatientAssociated.patientID
    })
    this.allDocuments = res.data
    this.processDocuments()
  }
}
</script>
<style scoped></style>
